/* eslint-disable array-bracket-newline */
/**
 * Compares current price with historical snapshot
 */
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IStore } from '../../reducers';
import { actionAddPriceChange } from '../../actions/price_changes';
import { KattanaAPI } from '../../store/createStore';

/**
 * Price change formulae
 * https://www.investopedia.com/terms/p/percentage-change.asp
 * increase = updated - original
 * increase % = (increase / original) * 100
 */

type changeType = {
  priceChange: number;
  priceChangePercent: number;
};

type usePriceChangeType = (
  currentPriceUSD: number,
  flipped: boolean,
  chain?: string,
  address?: string
) => changeType;

const usePriceChange: usePriceChangeType = (
  currentPriceUSD,
  flipped,
  chain = '',
  address = ''
) => {
  const [change, setChange] = useState<changeType>({
    priceChange: 0,
    priceChangePercent: 0,
  });
  const priceChanges = useSelector((state: IStore) => state.price_changes);
  const dispatch = useDispatch();

  const key = `${chain}-${address}`;
  const storedPrice = priceChanges[key]; // snapshot of yesterday price

  /**
   * If we don't have storedPrice and able to fetch than fetchs
   */
  useEffect(() => {
    let flag = true;

    if (!storedPrice && (chain != '' || address != '')) {
      // We can't fetch ticker data without chain, address or if it has already been fetched
      KattanaAPI
        .tickerYesterday(chain, address)
        .then((response: any) => {
          if (response && flag && response.status !== 'no_data') {
            dispatch(actionAddPriceChange(key, response));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }

    return () => {
      flag = false;
    };
  }, [storedPrice, chain, address]);

  /**
   * Work with data
   */
  useEffect(() => {
    if (!storedPrice) {
      setChange({
        priceChange: 0,
        priceChangePercent: 0,
      });
    } else {
      const yesterdayPriceUSD = flipped
        ? storedPrice.price_b_usd
        : storedPrice.price_a_usd;

      const priceDiff = currentPriceUSD - yesterdayPriceUSD;
      const priceDiffPer = parseFloat(
        ((priceDiff / yesterdayPriceUSD) * 100).toFixed(2)
      );

      setChange({
        priceChange: priceDiff,
        priceChangePercent: priceDiffPer,
      });
    }
  }, [flipped, currentPriceUSD, storedPrice]);

  return change;
};

export default usePriceChange;
